import { ref, computed } from "vue";

export function useLinkedInActions(
  reactiveContact: any,
  linkedInChannel: any,
  isChannelHealthyStatus: (status: string) => boolean,
  loadConversationsForChannel: (channel: string) => Promise<void>,
  toast: any
) {
  const isSendingLinkedInInvite = ref(false);
  const linkedInInviteError = ref<string | null>(null);
  const showLinkedInConnectionModal = ref(false);
  const showLinkedInProfileModal = ref(false);
  const showCompanyProfileModal = ref(false);

  const canSendLinkedInInvite = computed(() => {
    const linkedInUrl = reactiveContact.value?.linkedInUrl;
    const channelOk = isChannelHealthyStatus(linkedInChannel.value?.status);
    return !!linkedInUrl && !!linkedInChannel.value?.accountId && channelOk;
  });

  function extractLinkedInIdentifierFromUrl(linkedInUrl: string | null | undefined): string | null {
    if (!linkedInUrl || typeof linkedInUrl !== "string") return null;
    const raw = linkedInUrl.trim();
    if (!raw) return null;

    if (!raw.includes("linkedin.com")) {
      const candidate = raw.replace(/^@/, "").replace(/\/+$/, "").trim();
      return candidate || null;
    }

    let urlObj = null;
    try {
      urlObj = new URL(raw);
    } catch {
      try {
        urlObj = new URL(`https://${raw.replace(/^\/+/, "")}`);
      } catch {
        urlObj = null;
      }
    }
    if (!urlObj) return null;

    const path = (urlObj.pathname || "").replace(/\/+$/, "");

    const inMatch = path.match(/\/in\/([^/]+)/i);
    if (inMatch?.[1]) return decodeURIComponent(inMatch[1]);

    const companyMatch = path.match(/\/company\/([^/]+)/i);
    if (companyMatch?.[1]) return decodeURIComponent(companyMatch[1]);

    const salesPeopleMatch = path.match(/\/sales\/people\/([^/]+)/i);
    if (salesPeopleMatch?.[1]) return decodeURIComponent(salesPeopleMatch[1]);

    const parts = path.split("/").filter(Boolean);
    if (parts.length === 0) return null;
    return decodeURIComponent(parts[parts.length - 1]);
  }

  function handleLinkedInConnectionModalClose() {
    showLinkedInConnectionModal.value = false;
    linkedInInviteError.value = null;
    isSendingLinkedInInvite.value = false;
  }

  async function handleLinkedInConnectionSend(data: any) {
    isSendingLinkedInInvite.value = true;
    linkedInInviteError.value = null;

    const linkedInUrl = reactiveContact.value?.linkedInUrl;
    if (!linkedInUrl) {
      linkedInInviteError.value = "Please add a LinkedIn URL to the contact first.";
      isSendingLinkedInInvite.value = false;
      return;
    }

    if (!linkedInChannel.value?.accountId) {
      linkedInInviteError.value = "LinkedIn channel is not connected.";
      isSendingLinkedInInvite.value = false;
      return;
    }

    if (!isChannelHealthyStatus(linkedInChannel.value?.status)) {
      linkedInInviteError.value = "LinkedIn channel is disconnected. Please reconnect first.";
      isSendingLinkedInInvite.value = false;
      return;
    }

    const identifier = extractLinkedInIdentifierFromUrl(linkedInUrl);
    if (!identifier) {
      linkedInInviteError.value = "Invalid LinkedIn URL. Please update the contact.";
      isSendingLinkedInInvite.value = false;
      return;
    }

    if (data.message && data.message.length > 300) {
      linkedInInviteError.value = "Message must be 300 characters or less.";
      isSendingLinkedInInvite.value = false;
      return;
    }

    try {
      const Unipile = (await import("@/services/unipile/unipile")).default;
      let profileResponse;
      try {
        profileResponse = await Unipile.getProfile(identifier, {
          account_id: linkedInChannel.value.accountId,
          notify: false,
        });
      } catch (profileError) {
        console.error("Failed to get LinkedIn profile:", profileError);
        const profileErrorData = (profileError as any)?.response?.data;
        const profileErrorMessage =
          profileErrorData?.errorMessage ||
          profileErrorData?.message ||
          (profileError as any)?.message ||
          "User not found. The requested profile was not found.";
        linkedInInviteError.value = profileErrorMessage;
        isSendingLinkedInInvite.value = false;
        return;
      }

      const providerId = profileResponse?.data?.data?.provider_id;
      if (!providerId) {
        linkedInInviteError.value = "Could not resolve LinkedIn profile.";
        isSendingLinkedInInvite.value = false;
        return;
      }

      const invitationPayload: any = {
        provider_id: providerId,
        account_id: linkedInChannel.value.accountId,
      };

      if (data.message) {
        invitationPayload.message = data.message;
      }

      const response = await Unipile.sendInvitation(invitationPayload);
      showLinkedInConnectionModal.value = false;
      isSendingLinkedInInvite.value = false;
      linkedInInviteError.value = null;

      const invitationId = response?.data?.data?.invitation_id;
      const successMessage = invitationId
        ? `LinkedIn invitation sent successfully! (ID: ${invitationId})`
        : "LinkedIn invitation sent successfully!";
      toast.success(successMessage);

      await loadConversationsForChannel("linkedin");
    } catch (error) {
      console.error("Failed to send LinkedIn invite:", error);
      isSendingLinkedInInvite.value = false;

      const statusCode = (error as any)?.response?.status;
      const errorData = (error as any)?.response?.data;

      let errorMessage = "Failed to send LinkedIn invitation.";

      switch (statusCode) {
        case 400:
          errorMessage =
            errorData?.errorMessage ||
            errorData?.message ||
            "Invalid request. Please check your input and try again.";
          break;
        case 401:
          errorMessage =
            errorData?.errorMessage ||
            "Your LinkedIn account appears to be disconnected. Please reconnect your account.";
          break;
        case 403:
          errorMessage =
            errorData?.errorMessage ||
            "This feature is not available for your subscription or account type.";
          break;
        case 404:
          errorMessage =
            errorData?.errorMessage ||
            "LinkedIn account not found. Please check your account settings.";
          break;
        case 422:
          errorMessage =
            errorData?.errorMessage ||
            "Unable to send invitation. The recipient may already be a first-degree connection.";
          break;
        case 429:
          errorMessage =
            errorData?.errorMessage || "Too many requests. Please wait a moment and try again.";
          break;
        case 500:
        case 503:
        case 504:
          errorMessage =
            errorData?.errorMessage ||
            "LinkedIn service is temporarily unavailable. Please try again later.";
          break;
        default:
          errorMessage =
            errorData?.errorMessage ||
            errorData?.message ||
            (error as any)?.message ||
            "An unexpected error occurred. Please try again.";
      }

      linkedInInviteError.value = errorMessage;
    }
  }

  return {
    isSendingLinkedInInvite,
    linkedInInviteError,
    showLinkedInConnectionModal,
    showLinkedInProfileModal,
    showCompanyProfileModal,
    canSendLinkedInInvite,
    extractLinkedInIdentifierFromUrl,
    handleLinkedInConnectionModalClose,
    handleLinkedInConnectionSend,
  };
}
